import React, { MouseEventHandler, PropsWithChildren, useState } from 'react';
import { useSpring } from 'react-spring';
import { IconName } from '../icons';
import { TooltipBox, TooltipCard } from './styled';

export type ShowRule = 'hover' | 'click';
export type Position = 'top' | 'right' | 'bottom' | 'left';

export type TooltipProps = PropsWithChildren<{
  onClick?: MouseEventHandler<HTMLButtonElement>;
  title: string | React.ReactElement;
  showRule?: ShowRule;
  position?: Position;
  openByDefault?: boolean;
}>;

function TooltipMessage({
  position,
  children
}: React.PropsWithChildren<{ position: Position }>): JSX.Element {
  const style = useSpring({ to: { opacity: 1 }, from: { opacity: 0 } });

  return (
    <TooltipBox position={position} style={style}>
      {children}
    </TooltipBox>
  );
}

export function Tooltip(props: TooltipProps): JSX.Element {
  const {
    onClick,
    children,
    title,
    showRule = 'hover',
    position = 'top',
    openByDefault = false
  } = props;
  const [showTooltip, setShowTooltip] = useState(openByDefault);

  const handleShowingTooltip = (flag: boolean): void => {
    setShowTooltip(flag);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (showRule === 'click') {
      handleShowingTooltip(!showTooltip);
    }

    if (onClick) {
      onClick(e);
    }
  };

  const handleBlur = (): void => {
    if (showRule === 'click') {
      handleShowingTooltip(false);
    }
  };

  const handleMouseEnter = (): void => {
    if (showRule === 'hover') {
      handleShowingTooltip(true);
    }
  };

  const handleMouseLeave = (): void => {
    if (showRule === 'hover') {
      handleShowingTooltip(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>): void => {
    // Order of events: mousedown, blur, mouseup, click
    // e.preventDefault() on mouseDown prevents the blur from happening
    if (showRule === 'click') {
      e.preventDefault();
    }
  };

  return (
    <TooltipCard
      onBlur={handleBlur}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      variant="tertiary"
      size="small"
      icon={IconName.Info}
      type="button"
    >
      {title}
      {showTooltip && (
        <TooltipMessage position={position}>{children}</TooltipMessage>
      )}
    </TooltipCard>
  );
}
